import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Badge, Avatar, Field, Input, Select, Tabs, Drawer, Icon, IconBtn, EmptyState, PageHeader, Table, Spinner, Alert, Stat, Modal, ModalBody, ModalFooter, useToast, useConfirm } from "../../design-system";
import {
  createApiKey,
  createDocumentCategory,
  deleteDocumentCategory,
  deleteVacationPolicy,
  getDocumentCategories,
  getTimeTrackingSettings,
  getVacationPolicies,
  listApiKeys,
  removeMyAvatar,
  revokeApiKey,
  setMyAvatar,
  updateDocumentCategory,
  updateTimeTrackingSettings,
} from "../../helpers/kronaly_helper";
import { ME_AVATAR } from "../../helpers/url_helper";
import { formatInstant } from "../../helpers/datetime";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { SoporteConfigPanel } from "../SoporteIT/Configuracion";
import AvatarUploader from "../../Components/Kronaly/Avatares/AvatarUploader";
import VacationPolicyEditorModal from "../../Components/Kronaly/Vacaciones/VacationPolicyEditorModal";
import {
  ACCRUAL_UNITS,
  DAY_COUNT_BASES,
  MONTHS,
  summarizePolicy,
} from "../../Components/Kronaly/Vacaciones/vacationPolicyHelpers";

const unwrapData = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const findLabel = (list, value) =>
  list.find((o) => o.value === value)?.label || value || "—";

const SETTINGS_SECTIONS = [
  { id: "cuenta", label: "Mi cuenta", icon: "User" },
  { id: "empresa", label: "Datos de empresa", icon: "Building2" },
  { id: "horario", label: "Control horario", icon: "Clock" },
  { id: "vacaciones", label: "Vacaciones", icon: "Calendar" },
  { id: "overtime", label: "Horas extra", icon: "TrendingUp" },
  { id: "documentos", label: "Categorías de documentos", icon: "FolderTree" },
  { id: "geo", label: "Geolocalización", icon: "Settings" },
  { id: "notif", label: "Notificaciones", icon: "Bell" },
  { id: "integraciones", label: "Integraciones", icon: "Settings" },
];

function Configuracion() {
  document.title = "Configuración | Kronaly";
  const [section, setSection] = useState("cuenta");
  const { isAdmin } = useCurrentMembership();
  // La configuración de Soporte (técnicos + categorías) es solo owner/admin.
  const sections = isAdmin
    ? [...SETTINGS_SECTIONS, { id: "soporte", label: "Soporte", icon: "LifeBuoy" }]
    : SETTINGS_SECTIONS;
  return (
    <div className="kr-page">
      <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 4 }}>Workspace</div>
          <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>Configuración</h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)" }}>Reglas, políticas y datos generales de Acme Industries.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 16, alignItems: "start" }}>
          <Card padded={false} style={{ position: "sticky", top: 24 }}>
            {sections.map(s => (
              <div key={s.id} onClick={() => setSection(s.id)} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                borderLeft: section === s.id ? "3px solid var(--kr-action)" : "3px solid transparent",
                background: section === s.id ? "var(--kr-action-soft-bg)" : "transparent",
                color: section === s.id ? "var(--kr-action-soft-fg)" : "var(--kr-fg-primary)",
                fontSize: 13, fontWeight: section === s.id ? 600 : 500, cursor: "pointer",
              }}>
                <Icon name={s.icon} size={14} style={{ color: section === s.id ? "var(--kr-action)" : "var(--kr-fg-muted)" }} />
                {s.label}
              </div>
            ))}
          </Card>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {section === "cuenta" && <CuentaSection />}
            {section === "empresa" && <EmpresaSection />}
            {section === "horario" && <HorarioSection />}
            {section === "vacaciones" && <VacacionesSection />}
            {section === "overtime" && <OvertimeSection />}
            {section === "documentos" && <DocumentCategoriesSection />}
            {section === "geo" && <GeoSection />}
            {section === "notif" && <NotifSection />}
            {section === "integraciones" && <IntegracionesSection />}
            {section === "soporte" && <SoporteConfigPanel />}
          </div>
        </div>
      </div>
    </div>
  );
}

function CfgCard({ title, hint, children, action }) {
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{title}</div>
          {hint && <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>{hint}</div>}
        </div>
        {action}
      </div>
      {children}
    </Card>
  );
}

function CfgRow({ label, value, hint }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--kr-border-subtle)" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontFamily: "var(--kr-font-mono)" }}>{value}</div>
    </div>
  );
}

function ToggleRow({ label, hint, on }) {
  const [v, setV] = useState(on);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid var(--kr-border-subtle)" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>{hint}</div>}
      </div>
      <button onClick={() => setV(!v)} style={{
        width: 36, height: 20, borderRadius: 999, border: "none", cursor: "pointer",
        background: v ? "var(--kr-action)" : "var(--kr-neutral-300)",
        position: "relative", padding: 0,
      }}>
        <span style={{ position: "absolute", top: 2, left: v ? 18 : 2, width: 16, height: 16, borderRadius: 999, background: "#fff", transition: "left .12s" }} />
      </button>
    </div>
  );
}

function EmpresaSection() {
  return (
    <>
      <CfgCard title="Identidad" hint="Datos públicos visibles para empleados.">
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 20, alignItems: "center" }}>
          <div style={{ width: 96, height: 96, borderRadius: 12, background: "var(--kr-neutral-900)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 32, fontWeight: 700, fontFamily: "var(--kr-font-display)" }}>A</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FakeField label="Nombre" value="Acme Industries S.L." />
            <FakeField label="CIF" value="B-12345678" />
            <FakeField label="Sector" value="Tecnología" />
            <FakeField label="Tamaño" value="152 empleados" />
            <FakeField label="Email contacto" value="rrhh@acme.com" />
            <FakeField label="Teléfono" value="+34 910 000 000" />
          </div>
        </div>
      </CfgCard>

      <CfgCard title="Localización" hint="Determina festivos y zona horaria por defecto.">
        <CfgRow label="País" value="España" />
        <CfgRow label="Comunidad" value="Madrid" />
        <CfgRow label="Zona horaria" value="Europe/Madrid · UTC+01:00" />
        <CfgRow label="Idioma por defecto" value="Español" />
        <CfgRow label="Formato de fecha" value="DD/MM/YYYY" />
      </CfgCard>

      <CfgCard title="Sedes" hint="Localizaciones físicas para asignar a empleados." action={<Button variant="secondary" size="sm" icon="Plus">Añadir sede</Button>}>
        {[
          { name: "Madrid · HQ", address: "C/ Princesa 28, 28008 Madrid", count: 124 },
          { name: "Barcelona", address: "Av. Diagonal 405, 08008 Barcelona", count: 22 },
          { name: "Remoto", address: "Sin localización fija", count: 6 },
        ].map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < 2 ? "1px solid var(--kr-border-subtle)" : "none" }}>
            <div style={{ width: 32, height: 32, borderRadius: 6, background: "var(--kr-action-soft-bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="Building2" size={14} style={{ color: "var(--kr-action)" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{s.name}</div>
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{s.address}</div>
            </div>
            <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 600 }}>{s.count}</span>
            <IconBtn icon="Pencil" />
          </div>
        ))}
      </CfgCard>
    </>
  );
}

function HorarioSection() {
  return (
    <>
      <CfgCard title="Marcaje" hint="Cómo registran los empleados su jornada.">
        <ToggleRow label="Permitir marcaje desde móvil" hint="Empleados pueden iniciar/finalizar jornada desde la app." on={true} />
        <ToggleRow label="Permitir marcaje desde web" on={true} />
        <ToggleRow label="Marcaje obligatorio de pausas" hint="El empleado debe registrar manualmente sus descansos." on={false} />
        <ToggleRow label="Bloqueo de marcaje fuera de horario" hint="Bloquea marcajes 30 min antes/después del horario asignado." on={true} />
      </CfgCard>
      <CorrectionsSettingsCard />
      <CfgCard title="Validación de jornadas">
        <CfgRow label="Tolerancia de retraso" value="5 min" hint="Margen sin generar incidencia" />
        <CfgRow label="Jornada máxima diaria" value="12 h" hint="Por encima requiere justificación" />
        <CfgRow label="Pausa mínima entre jornadas" value="11 h" hint="Cumplimiento legal" />
      </CfgCard>
    </>
  );
}

function CorrectionsSettingsCard() {
  const toast = useToast();
  const membership = useCurrentMembership();
  const canEdit = membership.isAdmin; // owner+admin
  const [mode, setMode] = useState(null);
  const [graceDays, setGraceDays] = useState(10);
  const [graceDraft, setGraceDraft] = useState("10");
  const [warnMin, setWarnMin] = useState(10);
  const [warnDraft, setWarnDraft] = useState("10");
  const [autoCloseMin, setAutoCloseMin] = useState(30);
  const [autoCloseDraft, setAutoCloseDraft] = useState("30");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getTimeTrackingSettings()
      .then((r) => {
        if (cancelled) return;
        const data = unwrapData(r);
        setMode(data?.correction_mode || "requires_approval");
        const days = Number(data?.close_grace_days ?? 10);
        setGraceDays(days);
        setGraceDraft(String(days));
        const wm = Number(data?.shift_warning_minutes ?? 10);
        setWarnMin(wm);
        setWarnDraft(String(wm));
        const ac = Number(data?.auto_close_grace_minutes ?? 30);
        setAutoCloseMin(ac);
        setAutoCloseDraft(String(ac));
      })
      .catch((e) => !cancelled && setError(e?.message || "No se pudieron cargar los ajustes."))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const toggleRequiresApproval = async () => {
    if (!canEdit || saving) return;
    const next = mode === "requires_approval" ? "free" : "requires_approval";
    setSaving(true);
    setError(null);
    try {
      const r = await updateTimeTrackingSettings({ correction_mode: next });
      const data = unwrapData(r);
      setMode(data?.correction_mode || next);
      toast?.success?.(
        "Ajuste guardado",
        next === "requires_approval"
          ? "Las correcciones de los empleados ahora requerirán aprobación."
          : "Las correcciones de los empleados se aplican directamente.",
      );
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const saveGraceDays = async () => {
    const n = parseInt(graceDraft, 10);
    if (Number.isNaN(n) || n < 0 || n > 60) {
      setError("Los días de gracia deben estar entre 0 y 60.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await updateTimeTrackingSettings({ close_grace_days: n });
      const data = unwrapData(r);
      const saved = Number(data?.close_grace_days ?? n);
      setGraceDays(saved);
      setGraceDraft(String(saved));
      toast?.success?.("Días de gracia actualizados");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const saveWarnMin = async () => {
    const n = parseInt(warnDraft, 10);
    if (Number.isNaN(n) || n < 5 || n > 60) {
      setError("Los minutos de aviso previo deben estar entre 5 y 60.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await updateTimeTrackingSettings({ shift_warning_minutes: n });
      const data = unwrapData(r);
      const saved = Number(data?.shift_warning_minutes ?? n);
      setWarnMin(saved);
      setWarnDraft(String(saved));
      toast?.success?.("Aviso previo actualizado");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const saveAutoCloseMin = async () => {
    const n = parseInt(autoCloseDraft, 10);
    if (Number.isNaN(n) || n < 15 || n > 120) {
      setError("Los minutos de cierre automático deben estar entre 15 y 120.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await updateTimeTrackingSettings({ auto_close_grace_minutes: n });
      const data = unwrapData(r);
      const saved = Number(data?.auto_close_grace_minutes ?? n);
      setAutoCloseMin(saved);
      setAutoCloseDraft(String(saved));
      toast?.success?.("Cierre automático actualizado");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const on = mode === "requires_approval";

  return (
    <CfgCard
      title="Correcciones de jornada"
      hint="Parámetros del flujo de correcciones (cumplimiento de fichaje digital)."
    >
      {error && <Alert tone="danger" style={{ marginBottom: 8 }}>{error}</Alert>}

      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 0",
        borderBottom: "1px solid var(--kr-border-subtle)",
        opacity: loading ? 0.5 : 1,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>
            Las correcciones de los empleados requieren aprobación
          </div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            Si está activo, cuando un empleado corrija una jornada propia, quedará pendiente hasta que un manager
            u owner la apruebe. Si está desactivado, las auto-correcciones se aplican directamente al historial.
            En todos los casos, las correcciones que un manager realiza sobre la jornada de otro empleado siempre
            requieren la aprobación del empleado afectado.
          </div>
        </div>
        <button
          onClick={toggleRequiresApproval}
          disabled={!canEdit || saving || loading}
          title={canEdit ? "" : "Solo owners y administradores pueden cambiar este ajuste."}
          style={{
            width: 36, height: 20, borderRadius: 999, border: "none",
            cursor: (canEdit && !saving && !loading) ? "pointer" : "not-allowed",
            background: on ? "var(--kr-action)" : "var(--kr-neutral-300)",
            position: "relative", padding: 0,
            opacity: (canEdit && !saving) ? 1 : 0.7,
          }}
        >
          <span style={{
            position: "absolute", top: 2, left: on ? 18 : 2,
            width: 16, height: 16, borderRadius: 999, background: "#fff",
            transition: "left .12s",
          }} />
        </button>
      </div>

      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 0",
        opacity: loading ? 0.5 : 1,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>
            Días de gracia para cierre automático
          </div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            Cada noche, el sistema cierra automáticamente el mes anterior cuando han pasado N días desde su fin.
            Si hay correcciones pendientes, el cierre automático se salta hasta que se resuelvan.
            El cierre manual y la lista de meses se gestionan desde <strong>Jornada → Historial → Cierre mensual</strong>.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="number"
            min={0}
            max={60}
            value={graceDraft}
            disabled={!canEdit || saving || loading}
            onChange={(e) => setGraceDraft(e.target.value)}
            style={{
              width: 64, height: 32,
              border: "1px solid var(--kr-border)", borderRadius: 6,
              padding: "0 8px", fontSize: 13, textAlign: "center",
              fontFamily: "var(--kr-font-mono)",
            }}
          />
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>días</span>
          {canEdit && parseInt(graceDraft, 10) !== graceDays && (
            <Button variant="secondary" size="sm" onClick={saveGraceDays} disabled={saving}>
              Guardar
            </Button>
          )}
        </div>
      </div>

      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 0",
        borderTop: "1px solid var(--kr-border-subtle)",
        opacity: loading ? 0.5 : 1,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>
            Aviso previo de fin de jornada
          </div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            Minutos antes de completar las horas planificadas en los que se envía una notificación al empleado
            recordándole que su jornada está por finalizar.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="number"
            min={5}
            max={60}
            value={warnDraft}
            disabled={!canEdit || saving || loading}
            onChange={(e) => setWarnDraft(e.target.value)}
            style={{
              width: 64, height: 32,
              border: "1px solid var(--kr-border)", borderRadius: 6,
              padding: "0 8px", fontSize: 13, textAlign: "center",
              fontFamily: "var(--kr-font-mono)",
            }}
          />
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>min</span>
          {canEdit && parseInt(warnDraft, 10) !== warnMin && (
            <Button variant="secondary" size="sm" onClick={saveWarnMin} disabled={saving}>
              Guardar
            </Button>
          )}
        </div>
      </div>

      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 0",
        borderTop: "1px solid var(--kr-border-subtle)",
        opacity: loading ? 0.5 : 1,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>
            Cierre automático de turno
          </div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            Minutos de tolerancia tras completar la jornada planificada. Si el empleado no finaliza su turno
            ni solicita horas extras en ese plazo, el sistema cerrará el turno automáticamente.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="number"
            min={15}
            max={120}
            value={autoCloseDraft}
            disabled={!canEdit || saving || loading}
            onChange={(e) => setAutoCloseDraft(e.target.value)}
            style={{
              width: 64, height: 32,
              border: "1px solid var(--kr-border)", borderRadius: 6,
              padding: "0 8px", fontSize: 13, textAlign: "center",
              fontFamily: "var(--kr-font-mono)",
            }}
          />
          <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>min</span>
          {canEdit && parseInt(autoCloseDraft, 10) !== autoCloseMin && (
            <Button variant="secondary" size="sm" onClick={saveAutoCloseMin} disabled={saving}>
              Guardar
            </Button>
          )}
        </div>
      </div>
    </CfgCard>
  );
}


function VacacionesSection() {
  const toast = useToast();
  const confirm = useConfirm();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id,...} = edit
  const [showEditor, setShowEditor] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getVacationPolicies();
      const list = unwrapData(r) || [];
      setPolicies(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las políticas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSaved = (saved, isNew) => {
    if (!saved?.id) return;
    setPolicies((arr) => {
      if (isNew) return [...arr, saved];
      return arr.map((p) => (p.id === saved.id ? saved : p));
    });
    setShowEditor(false);
    setEditing(null);
  };

  const onDelete = async (policy) => {
    const ok = await confirm({
      title: "Eliminar política",
      description: `¿Seguro que quieres eliminar "${policy.name}"? Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteVacationPolicy(policy.id);
      setPolicies((arr) => arr.filter((p) => p.id !== policy.id));
      toast.success("Política eliminada", policy.name);
    } catch (e) {
      const msg = typeof e === "string" ? e : e?.message || "No se pudo eliminar la política.";
      toast.error("No se pudo eliminar", msg);
    }
  };

  return (
    <>
      <CfgCard
        title="Políticas de vacaciones"
        hint="Defínelas y asígnalas a los contratos. Cada política controla cómo se calculan días, devengo y arrastres."
        action={
          <Button
            variant="primary"
            size="sm"
            icon="Plus"
            onClick={() => { setEditing({}); setShowEditor(true); }}
          >
            Nueva política
          </Button>
        }
      >
        {error && <Alert tone="danger">{error}</Alert>}
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spinner size={16} />
          </div>
        ) : policies.length === 0 ? (
          <EmptyState
            icon="Calendar"
            title="Aún no hay políticas"
            description="Crea la primera política para empezar a asignarla a contratos."
            action={{
              label: "Crear política",
              icon: "Plus",
              onClick: () => { setEditing({}); setShowEditor(true); },
            }}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {policies.map((p) => (
              <VacationPolicyRow
                key={p.id}
                policy={p}
                onEdit={() => { setEditing(p); setShowEditor(true); }}
                onDelete={() => onDelete(p)}
              />
            ))}
          </div>
        )}
      </CfgCard>

      <VacationPolicyEditorModal
        open={showEditor}
        policy={editing}
        onClose={() => { setShowEditor(false); setEditing(null); }}
        onSaved={onSaved}
      />
    </>
  );
}

function VacationPolicyRow({ policy, onEdit, onDelete }) {
  const summary = summarizePolicy(policy);
  const cycle = `${findLabel(MONTHS, policy.cycle_start_month)} · ${policy.cycle_duration_months} meses`;
  const carry = policy.carryover_unlimited
    ? "Acumulación ilimitada"
    : Number(policy.carryover_amount) > 0
      ? `${Number(policy.carryover_amount)}${policy.accrual_type === "hours" ? "h" : "d"} arrastrables${policy.carryover_expiration_months ? ` · vencen en ${policy.carryover_expiration_months}m` : ""}`
      : "Sin arrastre";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 14px",
        border: "1px solid var(--kr-border-subtle)",
        borderRadius: 8,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>
            {policy.name}
          </div>
          <Badge tone="info">{findLabel(ACCRUAL_UNITS, policy.accrual_type)}</Badge>
          {policy.unlimited && <Badge tone="success">Ilimitado</Badge>}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            marginTop: 6,
            fontSize: 12,
            color: "var(--kr-fg-muted)",
          }}
        >
          <span>{summary}</span>
          <span>·</span>
          <span>{cycle}</span>
          <span>·</span>
          <span>{carry}</span>
          {policy.min_notice_days > 0 && (
            <>
              <span>·</span>
              <span>Antelación {policy.min_notice_days}d</span>
            </>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <Button variant="secondary" size="sm" icon="Pencil" onClick={onEdit}>
          Editar
        </Button>
        <IconBtn icon="Trash2" onClick={onDelete} title="Eliminar" />
      </div>
    </div>
  );
}

function OvertimeSection() {
  return (
    <CfgCard title="Reglas de horas extra">
      <ToggleRow label="Permitir horas extra" on={true} />
      <ToggleRow label="Requerir aprobación previa" on={true} />
      <CfgRow label="Compensación por defecto" value="Banco de horas" hint="Alternativa: pago directo" />
      <CfgRow label="Recargo nocturno" value="+25%" />
      <CfgRow label="Recargo festivos" value="+75%" />
      <CfgRow label="Tope mensual" value="20 h/mes" />
    </CfgCard>
  );
}

function GeoSection() {
  return (
    <>
      <CfgCard title="Geofencing" hint="Restringir marcaje a zonas autorizadas.">
        <ToggleRow label="Activar geofence" on={true} />
        <ToggleRow label="Marcaje remoto sin geofence" hint="Permitido para roles con teletrabajo configurado." on={true} />
      </CfgCard>
      <CfgCard title="Zonas autorizadas" action={<Button variant="secondary" size="sm" icon="Plus">Añadir zona</Button>}>
        {[
          { name: "Oficina Madrid", radius: "100 m", count: 124 },
          { name: "Oficina Barcelona", radius: "80 m", count: 22 },
          { name: "Cliente — Endesa", radius: "200 m", count: 4 },
        ].map((z, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < 2 ? "1px solid var(--kr-border-subtle)" : "none" }}>
            <div style={{ width: 32, height: 32, borderRadius: 999, background: "var(--kr-action-soft-bg)", border: "2px dashed var(--kr-action)" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{z.name}</div>
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>Radio {z.radius} · {z.count} empleados</div>
            </div>
            <IconBtn icon="Pencil" />
          </div>
        ))}
      </CfgCard>
    </>
  );
}

function NotifSection() {
  return (
    <CfgCard title="Notificaciones">
      <ToggleRow label="Email diario de pendientes" on={true} />
      <ToggleRow label="Push de marcaje sin cerrar" on={true} />
      <ToggleRow label="Slack del equipo en aprobaciones" on={false} />
      <ToggleRow label="Email semanal de cumplimiento" on={true} />
    </CfgCard>
  );
}

// Catálogo de scopes con etiquetas legibles. El servidor es la fuente de verdad
// (devuelve available_scopes); esto solo aporta nombre + descripción para la UI.
const API_SCOPES = [
  { value: "employees:read", label: "Empleados", desc: "Listado y detalle de empleados", endpoint: "GET /employees" },
  { value: "teams:read", label: "Equipos", desc: "Equipos del workspace y sus miembros", endpoint: "GET /teams · GET /teams/{id}/members" },
  { value: "time-tracking:read", label: "Horas trabajadas", desc: "Horas laboradas por día y empleado", endpoint: "GET /time-tracking/daily-hours" },
  { value: "absences:read", label: "Ausencias", desc: "Ausencias del workspace", endpoint: "GET /absences" },
];
const API_SCOPE_LABEL = Object.fromEntries(API_SCOPES.map((s) => [s.value, s.label]));

function apiKeyStatus(row) {
  if (row.revoked_at) return { tone: "danger", label: "Revocada" };
  if (!row.is_active) return { tone: "neutral", label: "Caducada" };
  return { tone: "success", label: "Activa" };
}

function IntegracionesSection() {
  const { isAdmin, loading: membershipLoading, timezone } = useCurrentMembership();
  const toast = useToast();
  const confirm = useConfirm();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listApiKeys();
      setKeys((r && r.data) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las API keys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!membershipLoading && isAdmin) load();
  }, [membershipLoading, isAdmin, load]);

  const handleRevoke = async (row) => {
    const ok = await confirm({
      title: `Revocar "${row.label}"`,
      description:
        "Cualquier aplicación que use esta API key dejará de tener acceso inmediatamente (401). La acción es irreversible.",
      confirmLabel: "Revocar API key",
      tone: "danger",
    });
    if (!ok) return;
    setSavingId(row.id);
    try {
      await revokeApiKey(row.id);
      toast.success("API key revocada", `"${row.label}" ya no es válida.`);
      await load();
    } catch (e) {
      toast.error("No se pudo revocar", typeof e === "string" ? e : e?.message || "Error desconocido");
    } finally {
      setSavingId(null);
    }
  };

  if (!membershipLoading && !isAdmin) {
    return (
      <CfgCard title="API Keys">
        <EmptyState
          icon="ShieldOff"
          title="Acceso restringido"
          description="Solo el propietario y los administradores del workspace pueden gestionar las API keys de integración."
        />
      </CfgCard>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <CfgCard
        title="API Keys"
        hint="Claves de acceso a la API de integración de Kronaly, para que otras aplicaciones (cuadros de mando, ETL…) lean tus datos en modo solo lectura."
        action={
          <Button variant="primary" icon="Plus" size="sm" onClick={() => setCreateOpen(true)}>
            Nueva API key
          </Button>
        }
      >
        <Alert tone="info">
          Cada clave concede solo los permisos (scopes) que elijas y se muestra <strong>una única vez</strong> al
          crearla. Guárdala en un sitio seguro; si la pierdes, revócala y crea otra.
        </Alert>

        {error && <div style={{ marginTop: 12 }}><Alert tone="danger">{error}</Alert></div>}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <Spinner />
          </div>
        ) : keys.length === 0 ? (
          <EmptyState
            icon="Key"
            title="Aún no hay API keys"
            description="Crea una para conectar una aplicación externa a la API de integración."
            action={{ label: "Nueva API key", icon: "Plus", onClick: () => setCreateOpen(true) }}
          />
        ) : (
          <div style={{ marginTop: 12 }}>
            <Table>
              <thead>
                <tr>
                  <th style={apiTh}>Etiqueta</th>
                  <th style={apiTh}>Clave</th>
                  <th style={apiTh}>Permisos</th>
                  <th style={apiTh}>Caduca</th>
                  <th style={apiTh}>Último uso</th>
                  <th style={apiTh}>Estado</th>
                  <th style={{ ...apiTh, textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((row) => {
                  const status = apiKeyStatus(row);
                  return (
                    <tr key={row.id}>
                      <td style={apiTd}><strong>{row.label}</strong></td>
                      <td style={apiTd}>
                        <code style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}>
                          {row.key_prefix}…
                        </code>
                      </td>
                      <td style={apiTd}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {(row.scopes || []).map((s) => (
                            <Badge key={s} tone="neutral">{API_SCOPE_LABEL[s] || s}</Badge>
                          ))}
                        </div>
                      </td>
                      <td style={apiTd}>
                        {row.expires_at
                          ? formatInstant(row.expires_at, timezone)
                          : <span style={{ color: "var(--kr-fg-muted)" }}>Sin caducidad</span>}
                      </td>
                      <td style={apiTd}>
                        {row.last_used_at
                          ? formatInstant(row.last_used_at, timezone)
                          : <span style={{ color: "var(--kr-fg-muted)" }}>Nunca usada</span>}
                      </td>
                      <td style={apiTd}><Badge tone={status.tone}>{status.label}</Badge></td>
                      <td style={{ ...apiTd, textAlign: "right" }}>
                        {!row.revoked_at && (
                          <IconBtn
                            icon="Ban"
                            title="Revocar"
                            onClick={() => handleRevoke(row)}
                            disabled={savingId === row.id}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </CfgCard>

      <ApiDocsCard />

      <CreateApiKeyDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
        toast={toast}
        tz={timezone}
      />
    </div>
  );
}

function ApiDocsCard() {
  return (
    <CfgCard
      title="Cómo usar la API"
      hint="API de solo lectura, distinta del backend interno. Autenticación por API key."
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13, color: "var(--kr-fg)" }}>
        <div>
          <strong>Base URL:</strong>{" "}
          <code style={apiInlineCode}>https://&lt;tu-dominio&gt;/api/v1/integration</code>
        </div>
        <div>
          <strong>Autenticación:</strong>{" "}
          <code style={apiInlineCode}>Authorization: Bearer kapi_…</code>
        </div>
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginBottom: 6 }}>Endpoints disponibles:</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {API_SCOPES.map((s) => (
              <div key={s.value} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <Badge tone="neutral">{s.value}</Badge>
                <code style={{ ...apiInlineCode, fontSize: 12 }}>{s.endpoint}</code>
              </div>
            ))}
          </div>
        </div>
        <pre style={apiCurlBox}>
{`curl -H "Authorization: Bearer kapi_…" \\
  "https://<tu-dominio>/api/v1/integration/time-tracking/daily-hours?start_date=2026-07-01&end_date=2026-07-07"`}
        </pre>
      </div>
    </CfgCard>
  );
}

function CreateApiKeyDrawer({ open, onClose, onCreated, toast, tz }) {
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState(API_SCOPES.map((s) => s.value));
  const [daysValid, setDaysValid] = useState("365");
  const [submitting, setSubmitting] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setLabel("");
      setScopes(API_SCOPES.map((s) => s.value));
      setDaysValid("365");
      setSubmitting(false);
      setCreatedKey(null);
      setCopied(false);
    }
  }, [open]);

  const toggleScope = (value) =>
    setScopes((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));

  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed || scopes.length === 0) return;
    setSubmitting(true);
    try {
      const payload = { label: trimmed, scopes };
      if (daysValid && daysValid !== "0") payload.days_valid = parseInt(daysValid, 10);
      const r = await createApiKey(payload);
      const data = r && typeof r === "object" && "data" in r ? r.data : r;
      setCreatedKey(data);
      await onCreated();
    } catch (e) {
      toast.error("No se pudo crear la API key", typeof e === "string" ? e : e?.message || "Error desconocido");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey?.plain_text) return;
    try {
      await navigator.clipboard.writeText(createdKey.plain_text);
      setCopied(true);
      toast.success("Copiada", "API key copiada al portapapeles.");
    } catch (_) {
      /* el usuario la copia manualmente */
    }
  };

  if (createdKey) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        title="API key creada"
        size="md"
        footer={<Button variant="primary" icon="Check" onClick={onClose}>He guardado la clave</Button>}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Alert tone="warning">
            <strong>Esta clave solo se muestra una vez.</strong> Cópiala ahora y guárdala en un sitio seguro
            (o en la configuración de la app que la vaya a usar). No se puede recuperar después.
          </Alert>

          <Field label="Etiqueta">
            <Input value={createdKey.label} readOnly />
          </Field>

          <Field label="API key">
            <div style={apiTokenBox}>
              <code style={apiTokenCode}>{createdKey.plain_text}</code>
              <Button
                variant={copied ? "ghost" : "primary"}
                icon={copied ? "Check" : "Copy"}
                onClick={handleCopy}
                size="sm"
              >
                {copied ? "Copiada" : "Copiar"}
              </Button>
            </div>
          </Field>

          <Field label="Permisos">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {(createdKey.scopes || []).map((s) => (
                <Badge key={s} tone="neutral">{API_SCOPE_LABEL[s] || s}</Badge>
              ))}
            </div>
          </Field>

          {createdKey.expires_at && (
            <Field label="Caduca">
              <Input value={formatInstant(createdKey.expires_at, tz)} readOnly />
            </Field>
          )}

          <Alert tone="info">
            <strong>Uso:</strong>
            <pre style={apiCurlBox}>
{`curl -H "Authorization: Bearer ${createdKey.plain_text}" \\
  https://<tu-dominio>/api/v1/integration/employees`}
            </pre>
          </Alert>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Nueva API key"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!label.trim() || scopes.length === 0 || submitting}
            icon="Key"
          >
            {submitting ? "Creando…" : "Crear API key"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Alert tone="info">
          Identifica la clave con la aplicación que la va a usar. Es lo que verás en el listado y en el log de accesos.
        </Alert>

        <Field label="Etiqueta" required>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ej. Cuadro de mando — Power BI"
            autoFocus
            maxLength={200}
          />
        </Field>

        <Field label="Permisos" description="La clave solo podrá acceder a los recursos que marques.">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {API_SCOPES.map((s) => {
              const active = scopes.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => toggleScope(s.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: `1px solid ${active ? "var(--kr-primary)" : "var(--kr-border)"}`,
                    background: active ? "var(--kr-primary-soft, var(--kr-bg-subtle))" : "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon
                    name={active ? "CheckSquare" : "Square"}
                    size={18}
                    style={{ color: active ? "var(--kr-primary)" : "var(--kr-fg-muted)", flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{s.desc}</div>
                  </div>
                  <code style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{s.value}</code>
                </button>
              );
            })}
          </div>
        </Field>

        <Field
          label="Validez"
          description="Tras este plazo la clave deja de funcionar. Puedes revocarla antes en cualquier momento."
        >
          <Select value={daysValid} onChange={(e) => setDaysValid(e.target.value)}>
            <option value="30">30 días</option>
            <option value="90">90 días</option>
            <option value="180">180 días</option>
            <option value="365">1 año (recomendado)</option>
            <option value="0">Sin caducidad</option>
          </Select>
        </Field>
      </div>
    </Drawer>
  );
}

const apiTh = {
  textAlign: "left",
  padding: "10px 14px",
  borderBottom: "1px solid var(--kr-border)",
  background: "var(--kr-bg-subtle)",
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
};

const apiTd = {
  padding: "12px 14px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "middle",
};

const apiTokenBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: 12,
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border)",
  borderRadius: 6,
};

const apiTokenCode = {
  flex: 1,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13,
  wordBreak: "break-all",
  color: "var(--kr-fg)",
};

const apiInlineCode = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border-subtle)",
  borderRadius: 4,
  padding: "1px 6px",
};

const apiCurlBox = {
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border)",
  borderRadius: 6,
  padding: 12,
  marginTop: 8,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
  whiteSpace: "pre-wrap",
  overflow: "auto",
};

function FakeField({ label, value }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500 }}>{value}</span>
    </label>
  );
}

// ─── Categorías de documentos ───────────────────────────────────────────

function DocumentCategoriesSection() {
  const toast = useToast();
  const confirm = useConfirm();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewRoot, setShowNewRoot] = useState(false);
  const [newSubParent, setNewSubParent] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getDocumentCategories();
      setTree(unwrapData(r) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las categorías.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmitRoot = async (name) => {
    try {
      await createDocumentCategory({ name });
      toast.success("Categoría creada");
      setShowNewRoot(false);
      load();
    } catch (e) {
      toast.error("No se pudo crear", typeof e === "string" ? e : e?.message);
    }
  };

  const onSubmitSub = async (parentId, name) => {
    try {
      await createDocumentCategory({ name, parent_id: parentId });
      toast.success("Subcategoría creada");
      setNewSubParent(null);
      load();
    } catch (e) {
      toast.error("No se pudo crear", typeof e === "string" ? e : e?.message);
    }
  };

  const onRename = async (id, name) => {
    try {
      await updateDocumentCategory(id, { name });
      toast.success("Renombrada");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("No se pudo renombrar", typeof e === "string" ? e : e?.message);
    }
  };

  const onDelete = async (cat) => {
    const ok = await confirm({
      title: "Eliminar categoría",
      description: `Eliminar "${cat.name}". Solo posible si no tiene documentos ni subcategorías.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteDocumentCategory(cat.id);
      toast.success("Categoría eliminada");
      load();
    } catch (e) {
      toast.error("No se pudo eliminar", typeof e === "string" ? e : e?.message);
    }
  };

  return (
    <CfgCard
      title="Categorías de documentos"
      hint="Organiza los documentos del workspace. Las categorías de sistema vienen pre-creadas y no se pueden eliminar; puedes añadir tus propias categorías y subcategorías."
      action={
        <Button variant="primary" size="sm" icon="Plus" onClick={() => setShowNewRoot(true)}>
          Nueva categoría
        </Button>
      }
    >
      {error && <Alert tone="danger">{error}</Alert>}
      {loading ? (
        <div style={{ padding: 24, textAlign: "center" }}><Spinner size={16} /></div>
      ) : tree.length === 0 && !showNewRoot ? (
        <EmptyState
          icon="FolderTree"
          title="Sin categorías"
          description="Crea la primera categoría para empezar."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {tree.map((cat) => (
            <CategoryRow
              key={cat.id}
              cat={cat}
              isRoot
              editing={editing}
              setEditing={setEditing}
              onRename={onRename}
              onDelete={onDelete}
              onAddSub={() => setNewSubParent(cat)}
            >
              {cat.children?.map((child) => (
                <CategoryRow
                  key={child.id}
                  cat={child}
                  isRoot={false}
                  editing={editing}
                  setEditing={setEditing}
                  onRename={onRename}
                  onDelete={onDelete}
                />
              ))}
              {newSubParent?.id === cat.id && (
                <InlineNewCategory
                  placeholder="Nombre de la subcategoría"
                  onSubmit={(name) => onSubmitSub(cat.id, name)}
                  onCancel={() => setNewSubParent(null)}
                  indent
                />
              )}
            </CategoryRow>
          ))}
          {showNewRoot && (
            <InlineNewCategory
              placeholder="Nombre de la categoría"
              onSubmit={onSubmitRoot}
              onCancel={() => setShowNewRoot(false)}
            />
          )}
        </div>
      )}
    </CfgCard>
  );
}

function CategoryRow({ cat, isRoot, editing, setEditing, onRename, onDelete, onAddSub, children }) {
  const isEditing = editing === cat.id;
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 10px",
          marginLeft: isRoot ? 0 : 24,
          borderLeft: isRoot ? "none" : "2px solid var(--kr-border-subtle)",
          background: isRoot ? "var(--kr-bg-subtle)" : "transparent",
          borderRadius: 6,
        }}
      >
        <Icon
          name={isRoot ? "Folder" : "FileText"}
          size={14}
          style={{ color: "var(--kr-fg-muted)", flexShrink: 0 }}
        />
        {isEditing ? (
          <InlineRenameInput
            initial={cat.name}
            onSubmit={(name) => onRename(cat.id, name)}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <>
            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>
              {cat.name}
            </span>
            {cat.is_system && (
              <Badge tone="neutral" variant="soft">Sistema</Badge>
            )}
            <div style={{ flex: 1 }} />
            {isRoot && onAddSub && (
              <IconBtn icon="Plus" aria-label="Añadir subcategoría" onClick={onAddSub} />
            )}
            <IconBtn icon="PenLine" aria-label="Renombrar" onClick={() => setEditing(cat.id)} />
            {!cat.is_system && (
              <IconBtn icon="Trash2" aria-label="Eliminar" onClick={() => onDelete(cat)} />
            )}
          </>
        )}
      </div>
      {children}
    </div>
  );
}

function InlineNewCategory({ placeholder, onSubmit, onCancel, indent = false }) {
  const [value, setValue] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const name = value.trim();
        if (name) onSubmit(name);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        marginLeft: indent ? 24 : 0,
      }}
    >
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        style={{ flex: 1 }}
      />
      <Button type="submit" variant="primary" size="sm">Crear</Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
    </form>
  );
}

function InlineRenameInput({ initial, onSubmit, onCancel }) {
  const [value, setValue] = useState(initial);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const name = value.trim();
        if (name && name !== initial) onSubmit(name);
        else onCancel();
      }}
      style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}
    >
      <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} style={{ flex: 1 }} />
      <Button type="submit" variant="primary" size="sm">Guardar</Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
    </form>
  );
}

function CuentaSection() {
  const { me, loading } = useCurrentMembership();
  // hasAvatar viene del /me; lo guardamos local para reflejar inmediatamente
  // los cambios tras subir/borrar sin re-fetchear /me.
  const [hasAvatar, setHasAvatar] = useState(false);
  useEffect(() => {
    setHasAvatar(!!me?.has_avatar);
  }, [me?.has_avatar]);

  if (loading) {
    return (
      <Card>
        <div style={{ padding: 24, textAlign: "center" }}><Spinner size={16} /></div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
            Tu foto de perfil
          </div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            Esta foto es global a tu cuenta y aparece en el Topbar y en
            workspaces donde aún no tengas perfil de empleado.
          </div>
        </div>
      </div>
      <AvatarUploader
        name={me?.name || "?"}
        hasAvatar={hasAvatar}
        avatarUrl={ME_AVATAR}
        canEdit
        onSet={async (file) => {
          await setMyAvatar(file);
          setHasAvatar(true);
          window.dispatchEvent(new CustomEvent("kronaly:avatar-changed", { detail: { has_avatar: true } }));
        }}
        onRemove={async () => {
          await removeMyAvatar();
          setHasAvatar(false);
          window.dispatchEvent(new CustomEvent("kronaly:avatar-changed", { detail: { has_avatar: false } }));
        }}
        size={96}
      />
      <div style={{ marginTop: 18, borderTop: "1px solid var(--kr-border-subtle)", paddingTop: 14 }}>
        <CfgRow label="Nombre" value={me?.name || "—"} />
        <CfgRow label="Email" value={me?.email || "—"} />
      </div>
    </Card>
  );
}

export default Configuracion;
